import { Refinement, z } from 'zod';
import { SignUpRequestCountryEnum } from '../openapi/yenta';
import {
  ALPHA_NUMERIC_REGEX_NO_SPACE,
  EMAIL_REGEX,
  NAME_REGEX,
} from '../utils/RegexUtils';
import { PASSWORD_VALIDATIONS } from '../utils/zodValidations';

interface CreateGenericSignUpSchemaParams {
  firstName?: boolean;
  lastName?: boolean;
  country?: boolean;
  username?: boolean;
  email?: boolean;
  password?: boolean;
  confirmPassword?: boolean;
  termsConsent?: boolean;
  callMessageConsent?: boolean;
}

interface RefinementParams {
  usernameRefinement?: Refinement<string>;
  emailRefinement?: Refinement<string>;
}

export const createGenericSignUpSchema = (
  enabledFields: CreateGenericSignUpSchemaParams,
  refinements?: RefinementParams,
) => {
  const schemaObject: Record<string, any> = {};

  if (enabledFields.firstName) {
    schemaObject.firstName = z
      .string({ required_error: 'Please enter first name' })
      .trim()
      .min(1, { message: 'Please enter first name' })
      .max(100, { message: 'Must be less than or equal to 100 characters' })
      .regex(RegExp(NAME_REGEX), {
        message: 'Please enter a valid first name',
      });
  }

  if (enabledFields.lastName) {
    schemaObject.lastName = z
      .string({ required_error: 'Please enter last name' })
      .trim()
      .min(1, { message: 'Please enter last name' })
      .max(100, { message: 'Must be less than or equal to 100 characters' })
      .regex(RegExp(NAME_REGEX), {
        message: 'Please enter a valid last name',
      });
  }

  if (enabledFields.country) {
    schemaObject.country = z.nativeEnum(SignUpRequestCountryEnum, {
      message: 'Please select country',
    });
  }

  if (enabledFields.username) {
    schemaObject.username = z
      .string({ required_error: 'Please enter username' })
      .trim()
      .min(2, { message: 'Username must be at least 2 characters' })
      .max(15, { message: 'Username must be fewer than 15 characters' })
      .refine((value) => ALPHA_NUMERIC_REGEX_NO_SPACE.test(value), {
        message: 'Username can only have letters and numbers',
      })
      .superRefine(async (value, ctx) => {
        if (refinements?.usernameRefinement) {
          const valid = await refinements.usernameRefinement(value, ctx);
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Username is already taken',
            });
          }
        }
      });
  }

  if (enabledFields.email) {
    schemaObject.emailAddress = z
      .string({ required_error: 'Please enter email address' })
      .trim()
      .min(1, { message: 'Please enter email address' })
      .refine((value) => EMAIL_REGEX.test(value), {
        message: 'Please enter valid email address',
      })
      .superRefine(async (value, ctx) => {
        if (refinements?.emailRefinement) {
          const valid = await refinements.emailRefinement(value, ctx);
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Email is already taken',
            });
          }
        }
      });
  }

  if (enabledFields.password) {
    schemaObject.password = PASSWORD_VALIDATIONS;
  }

  if (enabledFields.confirmPassword) {
    schemaObject.confirmPassword = z
      .string({ required_error: 'Please re-enter your password' })
      .trim()
      .min(1, { message: 'Please re-enter your password' });
  }

  if (enabledFields.termsConsent) {
    schemaObject.consentedToTerms = z
      .boolean({ required_error: 'Please provide your consent to continue' })
      .refine((value) => value === true, {
        message: 'Please provide your consent to continue',
      });
  }

  if (enabledFields.callMessageConsent) {
    schemaObject.consentedToCallMessage = z
      .boolean({ required_error: 'Please provide your consent to continue' })
      .refine((value) => value === true, {
        message: 'Please provide your consent to continue',
      });
  }

  const baseSchema = z.object(schemaObject);

  if (enabledFields.password && enabledFields.confirmPassword) {
    return baseSchema.superRefine(({ password, confirmPassword }, ctx) => {
      if (password && confirmPassword && password !== confirmPassword) {
        ctx.addIssue({
          path: ['confirmPassword'],
          code: z.ZodIssueCode.custom,
          message: 'Passwords do not match',
        });
      }
    });
  }

  return baseSchema;
};
